import { AlertCircle } from 'lucide-react';
import { useMemo } from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { useEmailTemplatesForKyc } from '@/pages/kyc/useEmailTemplatesForKyc';

import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';

interface EmailTemplateSelectorProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  /** React Hook Form control object */
  control: Control<TFieldValues>;
  /** Name of the form field */
  name: TName;
  /** Optional label for the field */
  label?: string;
  /** Optional placeholder text */
  placeholder?: string;
  /** Callback when template is selected */
  onTemplateSelect?: (templateId: string) => void;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
}

/**
 * EmailTemplateSelector Component
 *
 * A reusable email template selector component that fetches and displays
 * available email templates in a dropdown format. Integrates with React Hook Form
 * and handles loading/error states automatically.
 *
 * @component
 * @param {EmailTemplateSelectorProps} props - Component props
 * @returns {JSX.Element} Rendered email template selector
 *
 * @features
 * - **Template Loading**: Automatically fetches available email templates
 * - **Loading States**: Shows loading indicator while fetching templates
 * - **Error Handling**: Displays error state with retry functionality
 * - **Form Integration**: Full React Hook Form integration with validation
 * - **Rich Display**: Shows template name and subject in dropdown
 * - **Responsive Design**: Works well on all screen sizes
 * - **Internationalization**: Full translation support
 * - **Performance Optimized**: Uses memoization for expensive calculations
 *
 * @example
 * ```tsx
 * <EmailTemplateSelector
 *   control={form.control}
 *   name="emailTemplateId"
 *   label="Select Email Template"
 *   onTemplateSelect={(templateId) => console.log('Selected:', templateId)}
 * />
 * ```
 */
export function EmailTemplateSelector<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  onTemplateSelect,
  required = false,
  disabled = false,
}: EmailTemplateSelectorProps<TFieldValues, TName>) {
  const { t } = useTranslation();

  const {
    templateOptions,
    isLoading: isLoadingTemplates,
    error: templatesError,
    refetch: refetchTemplates,
  } = useEmailTemplatesForKyc();

  // Memoize computed values for performance
  const { fieldLabel, fieldPlaceholder, hasTemplates } = useMemo(
    () => ({
      fieldLabel: label || t('kyc.sendEmailDialog.fields.emailTemplate'),
      fieldPlaceholder:
        placeholder || t('kyc.sendEmailDialog.fields.emailTemplatePlaceholder'),
      hasTemplates: templateOptions.length > 0,
    }),
    [label, placeholder, t, templateOptions.length]
  );

  // Early returns for loading and error states
  if (isLoadingTemplates) {
    return <LoadingState message={t('emailTemplates.loading')} />;
  }

  if (templatesError) {
    return (
      <ErrorState
        error={templatesError}
        onRetry={refetchTemplates}
        title={t('error')}
      />
    );
  }

  // Show message when no templates are available
  if (!hasTemplates) {
    return (
      <div className='space-y-2'>
        <FormLabel>
          {fieldLabel}
          {required && <span className='text-destructive ml-1'>*</span>}
        </FormLabel>
        <div className='flex items-center gap-2 p-4 border border-border rounded-md bg-muted/50'>
          <AlertCircle className='h-5 w-5 text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>
            {t('kyc.sendEmailDialog.fields.noTemplatesAvailable')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        // Find the current selected template
        const currentTemplate = templateOptions.find(
          option => option.value === field.value
        );

        const handleValueChange = (value: string) => {
          field.onChange(value);
          onTemplateSelect?.(value);
        };

        return (
          <FormItem>
            <FormLabel>
              {fieldLabel}
              {required && <span className='text-destructive ml-1'>*</span>}
            </FormLabel>
            <Select
              onValueChange={handleValueChange}
              value={field.value}
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger
                  className={`h-auto ${currentTemplate ? 'min-h-[60px]' : 'min-h-[40px]'}`}
                >
                  {currentTemplate ? (
                    <div className='flex flex-col items-start w-full text-left'>
                      <span className='font-medium text-sm'>
                        {currentTemplate.label}
                      </span>
                      <span className='text-xs text-muted-foreground truncate max-w-full'>
                        {currentTemplate.subject}
                      </span>
                    </div>
                  ) : (
                    <SelectValue placeholder={fieldPlaceholder} />
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {templateOptions.map(option => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className='py-3'
                  >
                    <div className='flex flex-col items-start w-full'>
                      <span className='font-medium text-sm'>
                        {option.label}
                      </span>
                      <span className='text-xs text-muted-foreground truncate max-w-[280px] mt-0.5'>
                        {option.subject}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && (
              <p className='text-sm text-destructive flex items-center gap-1 mt-2'>
                <AlertCircle className='h-4 w-4' />
                {t(fieldState.error.message as string, {
                  defaultValue: fieldState.error.message as string,
                })}
              </p>
            )}
          </FormItem>
        );
      }}
    />
  );
}
